import React, { FC, memo, useEffect } from 'react';
import { NewTariff } from '../tariff.types';
import { useFormik } from 'formik';
import { Form, useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import cls from './CreateOrChangeTariffForm.module.scss';
import CreateTariffFormValuePart from '../CreateTariffFormValuePart';
import CreateTariffValidityPart from '../CreateTariffValidityPart';
import CreateTariffFormSettings from '../CreateTariffFormSettings';
import TariffAiSettings from '../TariffAiSettings';
import TariffStripeSettings from '../TariffStripeSettings';
import TariffReferralSettings from '../TariffReferralSettings';
import { imageUrlOrStaticPathValidator } from '../../../utils/imageValidation';

type CreateOrChangeTariffFormProps = {
  initialValues: NewTariff;
  disabled: boolean;
  handleSubmit: (values: NewTariff) => Promise<void>;
  isNew: boolean;
  setText: React.Dispatch<React.SetStateAction<string>>;
  text: string;
  className?: string;
  setDisabled: React.Dispatch<React.SetStateAction<boolean>>;
};

const CreateOrChangeTariffForm: FC<CreateOrChangeTariffFormProps> = memo(
  ({ initialValues, handleSubmit, isNew, setText, className, setDisabled, disabled, text }) => {
    const validationSchema = yup
      .object()
      .strict()
      .shape({
        name: yup.string().required('This field is required'),
        type: yup.string().required('This field is required'),
        cost: yup.number().min(0, 'Minimum value is 0'),
        label: yup.string().notRequired(),
        teaser: yup
          .string()
          .min(3, 'Minimum length is 3 characters')
          .max(65, 'Maximum length is 65 characters')
          .required('This field is required'),
        description: yup.string(),
        periodType: yup.string().required('This field is required'),
        periodAmount: yup.number().min(1, 'Minimum value is 1').required('This field is required'),
        imageSmall: imageUrlOrStaticPathValidator('imageSmall'),
        imageBig: imageUrlOrStaticPathValidator('imageBig'),
        topupDiscountPercent: yup
          .number()
          .min(0, 'Minimum value is 0')
          .max(100, 'Maximum value is 100'),
        referrerBonusTokens: yup
          .number()
          .integer('Must be a whole number')
          .min(0, 'Minimum value is 0'),
        inviteeBonusTokens: yup
          .number()
          .integer('Must be a whole number')
          .min(0, 'Minimum value is 0'),
        // the same shape apisupport enforces, empty included: empty is how a tariff is unbound
        // from Stripe, and a typo would otherwise be found by the consumer who cannot pay
        stripeProductId: yup
          .string()
          .matches(/^(|prod_[A-Za-z0-9_-]+)$/, 'Must look like prod_...'),
        stripePriceId: yup
          .string()
          .matches(/^(|price_[A-Za-z0-9_-]+)$/, 'Must look like price_...'),
      });
    const navigate = useNavigate();
    const formik = useFormik({
      initialValues,
      validationSchema,
      validateOnChange: false,
      onSubmit: async (values) => {
        await handleSubmit(values);
      },
    });
    const onChangeHandler = (event: React.ChangeEvent) => {
      formik.handleChange(event);
      formik.setErrors({});
    };
    useEffect(() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const handler = (e: any) => {
        if (e.target.closest('.tox-tinymce-aux, .moxman-window, .tam-assetmanager-root') !== null) {
          e.stopImmediatePropagation();
        }
      };
      document.addEventListener('focusin', handler);
      return () => document.removeEventListener('focusin', handler);
    }, []);
    useEffect(() => {
      if (
        formik.values.name !== initialValues.name ||
        formik.values.type !== initialValues.type ||
        formik.values.periodType !== initialValues.periodType ||
        formik.values.periodAmount !== initialValues.periodAmount ||
        formik.values.label !== initialValues.label ||
        formik.values.teaser !== initialValues.teaser ||
        formik.values.description !== initialValues.description ||
        formik.values.imageSmall !== initialValues.imageSmall ||
        formik.values.imageBig !== initialValues.imageBig ||
        formik.values.color !== initialValues.color ||
        formik.values.topupDiscountPercent !== initialValues.topupDiscountPercent ||
        formik.values.referrerBonusTokens !== initialValues.referrerBonusTokens ||
        formik.values.inviteeBonusTokens !== initialValues.inviteeBonusTokens ||
        formik.values.stripeProductId !== initialValues.stripeProductId ||
        formik.values.stripePriceId !== initialValues.stripePriceId ||
        formik.values.availableAiProviders !== initialValues.availableAiProviders
      ) {
        setDisabled(false);
      } else {
        setDisabled(true);
      }
    }, [formik.values, setDisabled, initialValues, text]);
    return (
      <Form
        onSubmit={formik.handleSubmit}
        className={`${className} ${cls.Form}`}
        data-testid="create-or-change-tariff-form"
      >
        <div className="d-flex gap-3">
          <CreateTariffFormValuePart
            nameError={formik.errors.name}
            nameValue={formik.values.name}
            costValue={formik.values.cost}
            labelValue={formik.values.label}
            teaser={formik.values.teaser}
            teaserError={formik.errors.teaser}
            descriptionValue={text}
            typeError={formik.errors.type}
            typeValue={formik.values.type}
            setText={setText}
            onChange={onChangeHandler}
            isNew={isNew}
            imageSmallValue={formik.values.imageSmall}
            imageSmallError={formik.errors.imageSmall}
            imageBigValue={formik.values.imageBig}
            imageBigError={formik.errors.imageBig}
            colorValue={formik.values.color}
          />
          <div className={cls.TariffFormRight}>
            <CreateTariffValidityPart
              periodExceedHoursError={formik.errors.periodExceedHours}
              periodExceedHoursValue={formik.values.periodExceedHours}
              periodAmountError={formik.errors.periodAmount}
              periodAmountValue={formik.values.periodAmount}
              periodTypeError={formik.errors.periodType}
              periodTypeValue={formik.values.periodType}
              onChange={onChangeHandler}
              isNew={isNew}
            />
            <CreateTariffFormSettings
              onChange={onChangeHandler}
              isNew={isNew}
              convertibleValue={formik.values.convertable}
              refundableValue={formik.values.refundable}
              renewableValue={formik.values.renewable}
            />
            <TariffAiSettings
              availableAiProviders={formik.values.availableAiProviders ?? []}
              setAvailableAiProviders={(aiProviders) =>
                formik.setFieldValue('availableAiProviders', aiProviders)
              }
              topupDiscountPercent={formik.values.topupDiscountPercent ?? 0}
              setTopupDiscountPercent={(percent) =>
                formik.setFieldValue('topupDiscountPercent', percent)
              }
              topupDiscountError={formik.errors.topupDiscountPercent}
            />
            <TariffReferralSettings
              referrerBonusTokens={formik.values.referrerBonusTokens ?? 0}
              setReferrerBonusTokens={(tokens) =>
                formik.setFieldValue('referrerBonusTokens', tokens)
              }
              referrerBonusError={formik.errors.referrerBonusTokens}
              inviteeBonusTokens={formik.values.inviteeBonusTokens ?? 0}
              setInviteeBonusTokens={(tokens) => formik.setFieldValue('inviteeBonusTokens', tokens)}
              inviteeBonusError={formik.errors.inviteeBonusTokens}
            />
            <TariffStripeSettings
              onChange={onChangeHandler}
              stripeProductIdValue={formik.values.stripeProductId}
              stripeProductIdError={formik.errors.stripeProductId}
              stripePriceIdValue={formik.values.stripePriceId}
              stripePriceIdError={formik.errors.stripePriceId}
            />
          </div>
        </div>
        <div className="d-flex gap-3">
          <button
            className={
              disabled ? `${cls.ActionButton} ${cls.ActionButtonDisabled}` : cls.ActionButton
            }
            type="submit"
            disabled={disabled}
          >
            {isNew ? 'Create' : 'Save Changes'}
          </button>
          <button
            type="button"
            onClick={() => {
              navigate(-1);
            }}
            className={cls.RevokeButton}
          >
            Cancel
          </button>
        </div>
      </Form>
    );
  },
);

export default CreateOrChangeTariffForm;
